/* global Event, HTMLElement, customElements, document, requestAnimationFrame */

if (!customElements.get('idb-contact-select')) {
  class IdreamBiznesDbSelect extends HTMLElement {
    constructor() {
      super();
      this.handleOutsidePointer = this.handleOutsidePointer.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleNativeChange = this.handleNativeChange.bind(this);
      this.handleNativeFocus = this.handleNativeFocus.bind(this);
      this.handleInvalid = this.handleInvalid.bind(this);
      this.handleFormReset = this.handleFormReset.bind(this);
    }

    connectedCallback() {
      if (!this.select) this.enhance();
      if (this.select && !this.listening) {
        document.addEventListener('pointerdown', this.handleOutsidePointer, true);
        this.form?.addEventListener('reset', this.handleFormReset);
        this.listening = true;
      }
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this.handleOutsidePointer, true);
      this.form?.removeEventListener('reset', this.handleFormReset);
      this.listening = false;
    }

    enhance() {
      const select = this.querySelector('select');
      if (!select || !select.id) return;

      const choices = Array.from(select.options).filter((option) => option.value && !option.disabled);
      if (!choices.length) return;

      this.select = select;
      this.form = select.form;
      this.choices = choices;
      this.activeIndex = -1;
      this.open = false;
      this.label = Array.from(select.form?.querySelectorAll('label') || []).find(
        (candidate) => candidate.htmlFor === select.id,
      );
      if (this.label && !this.label.id) this.label.id = `${select.id}-label`;

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'idb-contact__control idb-contact__select-trigger';
      trigger.id = `${select.id}-trigger`;
      trigger.setAttribute('role', 'combobox');
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', `${select.id}-listbox`);
      trigger.setAttribute('aria-required', String(select.required));
      trigger.disabled = select.disabled;

      const value = document.createElement('span');
      value.className = 'idb-contact__select-value';
      value.id = `${select.id}-value`;
      trigger.append(value);
      this.valueNode = value;
      if (this.label) trigger.setAttribute('aria-labelledby', `${this.label.id} ${value.id}`);
      else trigger.setAttribute('aria-label', select.options[0].textContent.trim());

      const chevron = document.createElement('span');
      chevron.className = 'idb-contact__select-chevron';
      chevron.setAttribute('aria-hidden', 'true');
      if (select.style.backgroundImage) chevron.style.backgroundImage = select.style.backgroundImage;
      trigger.append(chevron);

      const listbox = document.createElement('div');
      listbox.className = 'idb-contact__select-listbox';
      listbox.id = `${select.id}-listbox`;
      listbox.setAttribute('role', 'listbox');
      listbox.setAttribute('aria-label', this.label?.textContent.trim() || select.options[0].textContent.trim());
      listbox.hidden = true;

      this.optionNodes = choices.map((choice, index) => {
        const option = document.createElement('div');
        option.className = 'idb-contact__select-option';
        option.id = `${select.id}-option-${index}`;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');
        option.dataset.index = String(index);
        option.textContent = choice.textContent.trim();
        listbox.append(option);
        return option;
      });

      const status = document.createElement('span');
      status.className = 'idb-contact__visually-hidden';
      status.id = `${select.id}-status`;
      status.setAttribute('aria-live', 'assertive');
      trigger.setAttribute('aria-describedby', status.id);

      this.trigger = trigger;
      this.listbox = listbox;
      this.status = status;
      this.append(trigger, listbox, status);

      trigger.addEventListener('click', () => {
        if (this.open) this.close();
        else this.show();
      });
      trigger.addEventListener('keydown', this.handleKeydown);
      listbox.addEventListener('click', (event) => {
        const option = event.target.closest('[role="option"]');
        if (option && listbox.contains(option)) this.choose(Number(option.dataset.index));
      });
      select.addEventListener('change', this.handleNativeChange);
      select.addEventListener('focus', this.handleNativeFocus);
      select.addEventListener('invalid', this.handleInvalid);

      select.tabIndex = -1;
      select.setAttribute('aria-hidden', 'true');
      this.dataset.optionCount = String(choices.length);
      this.dataset.enhanced = 'true';
      this.syncFromNative();
    }

    handleNativeChange() {
      this.syncFromNative();
    }

    handleFormReset() {
      this.close();
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.syncFromNative();
        this.trigger.removeAttribute('aria-invalid');
        this.status.textContent = '';
      });
    }

    handleNativeFocus() {
      this.trigger.focus();
    }

    handleInvalid(event) {
      event.preventDefault();
      this.trigger.setAttribute('aria-invalid', 'true');
      this.status.textContent = `${this.label?.textContent.trim() || 'Wybierz opcję'}: wybierz opcję.`;
      if (this.select.form?.querySelector(':invalid') === this.select) {
        requestAnimationFrame(() => this.trigger.focus());
      }
    }

    handleOutsidePointer(event) {
      if (this.open && !this.contains(event.target)) this.close();
    }

    handleKeydown(event) {
      const { key } = event;
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Home' || key === 'End') {
        event.preventDefault();
        if (!this.open) this.show();
        if (key === 'Home') this.setActive(0);
        else if (key === 'End') this.setActive(this.choices.length - 1);
        else if (key === 'ArrowDown') this.setActive(Math.min(this.activeIndex + 1, this.choices.length - 1));
        else this.setActive(this.activeIndex < 0 ? this.choices.length - 1 : Math.max(this.activeIndex - 1, 0));
        return;
      }

      if (key === 'Enter' || key === ' ') {
        event.preventDefault();
        if (this.open) this.choose(this.activeIndex < 0 ? 0 : this.activeIndex);
        else this.show();
        return;
      }

      if (key === 'Escape' && this.open) {
        event.preventDefault();
        this.close();
        this.trigger.focus();
        return;
      }

      if (key === 'Tab' && this.open) this.close();
    }

    show() {
      if (this.trigger.disabled) return;
      this.open = true;
      this.dataset.open = 'true';
      this.activeIndex = this.choices.findIndex((choice) => choice.value === this.select.value);
      this.listbox.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      if (this.activeIndex >= 0) {
        this.trigger.setAttribute('aria-activedescendant', this.optionNodes[this.activeIndex].id);
      }
    }

    close() {
      this.open = false;
      this.removeAttribute('data-open');
      this.listbox.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.removeAttribute('aria-activedescendant');
      this.optionNodes.forEach((option) => option.removeAttribute('data-active'));
    }

    setActive(index) {
      this.activeIndex = index;
      this.optionNodes.forEach((option, optionIndex) => {
        if (optionIndex === index) option.dataset.active = 'true';
        else option.removeAttribute('data-active');
      });
      const active = this.optionNodes[index];
      this.trigger.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }

    choose(index) {
      const choice = this.choices[index];
      if (!choice) return;
      this.select.value = choice.value;
      this.select.dispatchEvent(new Event('input', { bubbles: true }));
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
      this.close();
      this.trigger.focus();
    }

    syncFromNative() {
      const selectedIndex = this.choices.findIndex((choice) => choice.value === this.select.value);
      this.valueNode.textContent =
        selectedIndex < 0 ? this.select.options[0].textContent.trim() : this.choices[selectedIndex].textContent.trim();
      this.optionNodes.forEach((option, index) => {
        option.setAttribute('aria-selected', String(index === selectedIndex));
      });
      if (selectedIndex >= 0) {
        this.trigger.removeAttribute('aria-invalid');
        this.status.textContent = '';
      }
    }
  }

  customElements.define('idb-contact-select', IdreamBiznesDbSelect);
}
